/** Audience is browsing context, not product classification or customer data. */
const AUDIENCES = new Set(['women', 'men']);
const CONTEXT_PARAMETER = 'muuv_audience';

export function resolveAudience({ pageType, serverAudience, productAudience }, requestedAudience) {
  const server = AUDIENCES.has(serverAudience) ? serverAudience : 'neutral';
  if (pageType !== 'product') return server;
  // Household and children never inherit an adult department. Verified single-
  // audience tags also win over an incompatible incoming browsing context.
  if (productAudience === 'blocked') return 'neutral';
  if (AUDIENCES.has(productAudience)) return productAudience;
  if (AUDIENCES.has(requestedAudience)) return requestedAudience;
  return 'neutral';
}

export function contextualProductHref(href, audience, baseUrl, productAudience) {
  let url;
  let base;
  try {
    base = new URL(baseUrl);
    url = new URL(href, base);
  } catch {
    return href;
  }
  // Never decorate external sites, cart/checkout actions, or non-product pages.
  if (url.origin !== base.origin || !/\/products\/[^/.]+\/?$/.test(url.pathname)) return href;
  // A curated hero can feature another department on the default Women's
  // homepage. Only a verified single-audience tag may override that context.
  const linkAudience = AUDIENCES.has(productAudience) ? productAudience : audience;
  if (AUDIENCES.has(linkAudience)) url.searchParams.set(CONTEXT_PARAMETER, linkAudience);
  else url.searchParams.delete(CONTEXT_PARAMETER);
  return url.href;
}

function startNavigation() {
  let currentAudience = 'neutral';
  const rootSelector = '[data-muuv-audience-navigation]';
  const getHeader = () => document.querySelector(`${rootSelector}[data-presentation="header"]`);

  function decorateLinks(container = document) {
    const links = [];
    if (container.matches?.('a[href]')) links.push(container);
    links.push(...(container.querySelectorAll?.('a[href*="/products/"]') || []));
    for (const link of links) {
      if (link.hasAttribute('download')) continue;
      const href = link.getAttribute('href');
      const next = contextualProductHref(href, currentAudience, window.location.href, link.dataset.muuvProductAudience);
      if (next !== href) link.setAttribute('href', next);
    }
  }

  function applyState(nav, audience) {
    nav.dataset.audience = audience;
    for (const item of nav.querySelectorAll('[data-nav-audience]')) {
      const scope = item.dataset.navAudience;
      item.hidden = scope !== 'global' && audience !== 'neutral' && scope !== audience;
    }
    for (const link of nav.querySelectorAll('[data-audience-link]')) {
      link.classList.toggle('muuv-marketplace-audience__item--active', link.dataset.audienceLink === audience);
      // A PDP is not the audience collection itself. Avoid a false aria-current.
      const current = nav.dataset.pageType === 'collection' && new URL(link.href).pathname === window.location.pathname;
      if (current) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    }
  }

  function syncNavigation() {
    const header = getHeader();
    const roots = [...document.querySelectorAll(rootSelector)];
    const source = header || roots[0];
    if (!source) return;
    const requested = new URL(window.location.href).searchParams.get(CONTEXT_PARAMETER);
    currentAudience = resolveAudience(source.dataset, requested);
    for (const nav of roots) {
      if (header && nav !== header && nav.dataset.presentation === 'drawer') {
        // One configured link set for both surfaces. Liquid defaults still give
        // usable Women/Men/shared destinations when scripting is disabled.
        nav.replaceChildren(...[...header.children].map((child) => child.cloneNode(true)));
        for (const item of nav.querySelectorAll('[data-shopify-editor-block]')) item.removeAttribute('data-shopify-editor-block');
        for (const landmark of nav.querySelectorAll('nav[aria-label]')) {
          landmark.setAttribute('aria-label', `Menu: ${landmark.getAttribute('aria-label')}`);
        }
        nav.dataset.pageType = header.dataset.pageType;
      }
      applyState(nav, currentAudience);
    }
    decorateLinks();
  }

  syncNavigation();
  window.addEventListener('pageshow', syncNavigation);
  window.addEventListener('popstate', syncNavigation);
  document.addEventListener('shopify:section:load', syncNavigation);

  // Product swatches and section-rendered search/pagination replace hrefs or
  // entire cards. Preserve variant/query/preview parameters during enhancement.
  const observer = new MutationObserver((records) => {
    let navigationChanged = false;
    for (const record of records) {
      if (record.type === 'attributes') decorateLinks(record.target);
      for (const node of record.addedNodes || []) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (node.matches?.(rootSelector) || node.querySelector?.(rootSelector)) navigationChanged = true;
        else decorateLinks(node);
      }
    }
    if (navigationChanged) syncNavigation();
  });
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startNavigation, { once: true });
  else startNavigation();
}
